/*
 * Vehicle search form modal
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2024 Gallagher Bassett Services, Inc.
 */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  VehicleService,
  JurisdictionService
} from 'app/shared/services';
import { take } from 'rxjs/operators';
import {
  Vehicle,
  Enum
} from 'app/shared/models';
import { NotificationService } from 'app/shared/services/notification.service';

@Component ( {
    selector: 'reporter-vehicles-search',
    templateUrl: './vehicle-search.component.html',
    styleUrls: ['./vehicle-search.component.scss'],
    standalone: false
} )
export class VehicleSearchComponent implements OnChanges, OnInit {

  @Input () show = false;
  @Output () close = new EventEmitter<boolean> ();
  @Output () selectVehicle = new EventEmitter<Vehicle> ();

  vehicles: Vehicle[];
  searchModel: any = { includeDescendants: true };
  jurisdictions: Enum[];
  pageNumber = 1;
  totalRecords = 0;
  totalPages = 0;
  details: Vehicle;
  timeOut;
  selectedOnHover = false;
  inFocus: Vehicle;
  vehiclesSearchOpen = true;

  constructor (
    private vehicleService: VehicleService,
    private jurisdictionService: JurisdictionService,
    private notificationService: NotificationService,
  ) {
  }

  ngOnInit () {
    this.jurisdictionService.allByCountry ()
      .pipe ( take ( 1 ) )
      .subscribe (
        jurisdictions => this.jurisdictions = jurisdictions
      );
  }

  ngOnChanges ( changes: SimpleChanges ): void {
  }

  search () {
    this.notificationService.clearAll ();
    this.vehicles = undefined;
    this.pageNumber = 1;

    this.vehicleService.search ( this.searchModel, this.pageNumber )
      .pipe ( take ( 1 ) )
      .subscribe ( result => {
        this.vehicles = result.records;
        this.totalPages = result.totalPages;
        this.totalRecords = result.totalRecords;
        this.pageNumber = result.currentPage;

        this.vehiclesSearchOpen = false;
      } );
  }

  loadNextPage () {
    this.pageNumber++;
    this.vehicleService.search ( this.searchModel, this.pageNumber )
      .pipe ( take ( 1 ) )
      .subscribe ( results => {
        this.vehicles = [ ...this.vehicles, ...results.records ];
      } );
  }

  async getDetails ( c: Vehicle ) {
    this.selectedOnHover = false;
    if ( c?.id === this.details?.id ) {
      this.details = undefined;
      this.inFocus = undefined;
    } else {
      this.details = { ...c };
    }
  }

  mouseOver ( c: Vehicle ) {
    if ( c?.id !== this.details?.id ) {
      if ( this.timeOut ) {
        clearTimeout ( this.timeOut );
      }
      this.timeOut = setTimeout ( async () => {
        await this.getDetails ( c );
        this.selectedOnHover = true;
      }, 2000 );
    }
  }

  unsetTimeOut ( c: Vehicle ) {
    if ( this.timeOut ) {
      clearTimeout ( this.timeOut );
    }
    if ( this.details && c?.id === this.details?.id && this.selectedOnHover ) {
      this.selectedOnHover = false;
      this.details = undefined;
      this.inFocus = undefined;
    }
  }

  select ( c: Vehicle ) {
    this.selectVehicle.emit ( c );
    this.inFocus = undefined;
  }

  resetForm () {
    this.searchModel = {};
    this.vehicles = undefined;
    this.notificationService.clearAll ();
  }
}
